import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import FormControl from '@mui/material/FormControl';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import FormHelperText from '@mui/material/FormHelperText';
import { type Theme } from '@mui/material/styles';
import { memo, useCallback, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import { useNavigate, useSearchParams } from 'react-router-dom';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';

import { IconEyeOff as VisibilityOff } from '@/components/icon/tabler/IconEyeOff';
import { IconEye as Visibility } from '@/components/icon/tabler/IconEye';
import { IconMail as MailOutlineRoundedIcon } from '@/components/icon/tabler/IconMail';
import { IconCircleCheck as CheckCircleIcon } from '@/components/icon/tabler/IconCircleCheck';
import Button from '@/components/Button';
import AuthAccessCard from '@/domain/auth/components/AuthAccessCard';
import AuthFieldError from '@/domain/auth/components/AuthFieldError';
import AuthForm from '@/domain/auth/components/AuthForm';
import AuthPageLayout from '@/domain/auth/components/AuthPageLayout';
import SignUpSuccessModal from '@/domain/auth/components/SignUpSuccessModal';
import { signupSchema } from '@/domain/auth/schemas';
import { type SignupFormData } from '@/domain/auth/types';
import { toSignupRequest } from '@/domain/auth/utils';
import { getInviteEmail } from '@/domain/invite/utils';
import { useSignup } from '@/api/hooks';
import { ScreenRoutes } from '@/types/router';
import { BODYPRINT_PRIVACY_URL } from '@/config/links';
import { useTranslate } from '@/state/i18n';
import { controlTokens } from '@/theme/themePrimitives';
import { normalizeApiError } from '@/utils/error';

const SignUp = () => {
  const t = useTranslate('domain.auth.SignUp');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { mutateAsync: signup, isPending } = useSignup();
  const [signedUpEmail, setSignedUpEmail] = useState<string | null>(null);
  const [signupError, setSignupError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState({
    password: false,
    confirmPassword: false,
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<SignupFormData>({
    resolver: joiResolver(signupSchema),
    // on blur used to auth related cards
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      // Coming from a partner invite, the invited e-mail is already filled in.
      email: getInviteEmail(searchParams),
      firstName: '',
      lastName: '',
      password: '',
      confirmPassword: '',
      acceptPrivacyPolicy: false,
      receivePlatformUpdateEmails: false,
    },
  });

  const onSubmit = useCallback(
    async (values: SignupFormData) => {
      setSignupError(null);
      try {
        await signup(toSignupRequest(values));
        setSignedUpEmail(values.email);
      } catch (error) {
        setSignupError(normalizeApiError(error).userMessage);
      }
    },
    [signup],
  );

  return (
    <AuthPageLayout variant="signup">
      <AuthAccessCard active="signup">
        <AuthForm onSubmit={handleSubmit(onSubmit)}>
          {signupError ? (
            <Alert severity="error" role="alert">
              {signupError}
            </Alert>
          ) : null}
          <Box sx={sx.head}>
            <Typography component="h2" variant="h2" sx={sx.title}>
              {t('title')}
            </Typography>
            <Typography variant="body2" sx={sx.muted}>
              {t('subtitle')}
            </Typography>
          </Box>
          <Box sx={sx.nameRow}>
            <FormControl>
              <FormLabel htmlFor="firstName">{t('firstName')}</FormLabel>
              <Controller
                name="firstName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    error={!!errors.firstName}
                    helperText={
                      errors.firstName && <AuthFieldError message={errors.firstName.message} />
                    }
                    autoComplete="given-name"
                    required
                    fullWidth
                    placeholder={t('firstNamePlaceholder')}
                    id="firstName"
                    color={errors.firstName ? 'error' : 'primary'}
                  />
                )}
              />
            </FormControl>
            <FormControl>
              <FormLabel htmlFor="lastName">
                {t('lastName')}{' '}
                <Box component="span" sx={sx.optional}>
                  {t('optional')}
                </Box>
              </FormLabel>
              <Controller
                name="lastName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    error={!!errors.lastName}
                    helperText={
                      errors.lastName && <AuthFieldError message={errors.lastName.message} />
                    }
                    autoComplete="family-name"
                    fullWidth
                    id="lastName"
                    placeholder={t('lastNamePlaceholder')}
                    color={errors.lastName ? 'error' : 'primary'}
                  />
                )}
              />
            </FormControl>
          </Box>
          <FormControl>
            <FormLabel htmlFor="email">{t('email')}</FormLabel>
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  error={!!errors.email}
                  helperText={errors.email && <AuthFieldError message={errors.email.message} />}
                  required
                  fullWidth
                  id="email"
                  type="email"
                  placeholder={t('emailPlaceholder')}
                  autoComplete="email"
                  variant="outlined"
                  color={errors.email ? 'error' : 'primary'}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <MailOutlineRoundedIcon />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              )}
            />
          </FormControl>
          <Box sx={sx.passwordRow}>
            <FormControl>
              <FormLabel htmlFor="password">{t('password')}</FormLabel>
              <Controller
                name="password"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    error={!!errors.password}
                    helperText={
                      errors.password && <AuthFieldError message={errors.password.message} />
                    }
                    required
                    fullWidth
                    type={showPassword.password ? 'text' : 'password'}
                    id="password"
                    autoComplete="new-password"
                    variant="outlined"
                    color={errors.password ? 'error' : 'primary'}
                    placeholder="••••••••"
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label={
                                showPassword.password ? t('hidePassword') : t('showPassword')
                              }
                              onClick={() =>
                                setShowPassword((prev) => ({ ...prev, password: !prev.password }))
                              }
                              edge="end"
                              tabIndex={-1}
                            >
                              {showPassword.password ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                )}
              />
            </FormControl>
            <FormControl>
              <FormLabel htmlFor="confirmPassword">{t('confirmPassword')}</FormLabel>
              <Controller
                name="confirmPassword"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    error={!!errors.confirmPassword}
                    helperText={
                      errors.confirmPassword && (
                        <AuthFieldError message={errors.confirmPassword.message} />
                      )
                    }
                    required
                    fullWidth
                    type={showPassword.confirmPassword ? 'text' : 'password'}
                    id="confirmPassword"
                    autoComplete="new-password"
                    variant="outlined"
                    color={errors.confirmPassword ? 'error' : 'primary'}
                    placeholder={t('confirmPasswordPlaceholder')}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label={
                                showPassword.confirmPassword ? t('hidePassword') : t('showPassword')
                              }
                              onClick={() =>
                                setShowPassword((prev) => ({
                                  ...prev,
                                  confirmPassword: !prev.confirmPassword,
                                }))
                              }
                              edge="end"
                              tabIndex={-1}
                            >
                              {showPassword.confirmPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                )}
              />
            </FormControl>
          </Box>
          <Box sx={sx.consents}>
            <Controller
              name="receivePlatformUpdateEmails"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Checkbox {...field} checked={field.value} color="primary" />}
                  label={t('marketingOptIn')}
                />
              )}
            />
            <FormControl>
              <Controller
                name="acceptPrivacyPolicy"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        {...field}
                        checked={field.value}
                        color={errors.acceptPrivacyPolicy ? 'error' : 'primary'}
                      />
                    }
                    label={
                      <>
                        {t('acceptPrivacyLead')}
                        <Link
                          href={BODYPRINT_PRIVACY_URL}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-link-tone="dark"
                        >
                          {t('privacyPolicy')}
                        </Link>
                      </>
                    }
                  />
                )}
              />
              {errors.acceptPrivacyPolicy && (
                <FormHelperText error sx={sx.consentError}>
                  <AuthFieldError message={errors.acceptPrivacyPolicy.message} />
                </FormHelperText>
              )}
            </FormControl>
          </Box>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            loading={isPending}
            disabled={Boolean(signedUpEmail)}
          >
            {t('submit')}
          </Button>
          <Box sx={sx.activationNote}>
            <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main', flexShrink: 0 }} />
            <Typography variant="caption" color="text.secondary">
              {t('activationEmailNote')}
            </Typography>
          </Box>
        </AuthForm>
        <SignUpSuccessModal
          open={Boolean(signedUpEmail)}
          email={signedUpEmail ?? watch('email')}
          onGoToLogin={() =>
            navigate({ pathname: ScreenRoutes.SIGNIN, search: searchParams.toString() })
          }
        />
      </AuthAccessCard>
    </AuthPageLayout>
  );
};

export default memo(SignUp);

const sx = {
  activationNote: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 0.75,
    mt: -1,
  },
  head: { display: 'flex', flexDirection: 'column', gap: '8px', mb: 0.5 },
  title: (theme: Theme) => ({
    fontSize: { xs: 21, sm: 24 },
    lineHeight: 1.25,
    letterSpacing: '-0.012em',
    color: controlTokens.primary,
    ...theme.applyStyles('dark', { color: 'text.primary' }),
  }),
  muted: { color: 'text.secondary', fontSize: { xs: 13, sm: 14 } },
  optional: { color: 'text.secondary', fontSize: 13, fontWeight: 400 },
  nameRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: { xs: '10px', sm: '12px' },
  },
  passwordRow: {
    display: 'grid',
    gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' },
    gap: { xs: 2, sm: '12px' },
  },
  // The native radio fills the pill invisibly, so the whole pill is the hit area.
  consents: { display: 'flex', flexDirection: 'column', gap: 1.5, mt: 0.5 },
  consentError: { pl: '28px' },
};
